import type { FC, ReactNode } from 'react';
import { useId } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { Tag, TagGroup, TagList } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { CopyButton } from '../../internal/components/AgentInsights/components/CopyButton';

import { MessageFeedback } from './MessageFeedback';
import type { MessageMetaItem } from './MessageMeta';
import { MessageMeta } from './MessageMeta';

import {
  card,
  cardAgent,
  cardAgentChat,
  cardBelow,
  cardBelowEnd,
  cardBelowRedesign,
  cardBelowStart,
  cardBody,
  cardHeader,
  cardHeaderExtras,
  cardHeaderMain,
  cardHeaderMainWrap,
  cardTail,
  cardTailFill,
  cardTailStroke,
  cardTailSvg,
  cardTurnAgent,
  cardUser,
  cardUserBubble,
  cardUserCompact,
  copyAffordance,
  copyAffordanceRedesign,
  copyHoverGroup,
  meta as metaClass,
  metaItem,
  modelChip,
  modelChipIcon,
  TAIL_SIZE,
  turn,
  turnAgent,
  turnAgentChat,
  turnCaptionless,
  turnUser,
  turnUserCompact,
  userCopy,
} from '../../internal/components/conversation/ConversationChrome.css';

export type ConversationMessageSpeaker = 'user' | 'agent';
export type ConversationChromeVariant = 'transcript' | 'chat';

export interface ConversationMessageCardProps {
  speaker: ConversationMessageSpeaker;
  /**
   * `transcript` (default) is the Read-transcript chrome: User/Agent tags and
   * a bordered agent card. `chat` is Vega-only: no role tags, flush agent replies.
   */
  variant?: ConversationChromeVariant;
  /** Secondary chip on agent messages (chip-ai + model id). */
  model?: string;
  children: ReactNode;
  /** Non-interactive chips (e.g. eval badges) in the header. */
  badges?: ReactNode;
  /** Sibling controls outside the role/model header (e.g. span icon buttons). */
  headerExtra?: ReactNode;
  /** Copy affordance (or similar) — shares the caption row with meta. */
  footer?: ReactNode;
  meta?: MessageMetaItem[];
  metaTitle?: string;
  className?: string;
  /** Hide the caption row until the turn is hovered or focused. */
  hoverRevealCaption?: boolean;
  density?: 'default' | 'compact';
  /** Redesigned transcript chrome (`vega-conversation-transcript-redesign`). Off renders main's legacy card. */
  redesign?: boolean;
  time?: string;
  copyText?: string;
  onCopy?: () => void;
  onShowDetails?: () => void;
}

const SPEAKER_TAG: Record<ConversationMessageSpeaker, { icon: 'person-outline' | 'robot'; label: string }> = {
  user: { icon: 'person-outline', label: 'User' },
  agent: { icon: 'robot', label: 'Agent' },
};

/**
 * Shared user/agent message chrome: role Tag, optional model chip, body, optional
 * footer, and quiet meta caption. Indent and surface follow the conversation Figma.
 * Not selectable — span drill-in and Inspect use their own controls.
 */
type SpeakerClassNames = { user: string; userCompact: string; agentChat: string; agent: string };

const TURN_CLASSES: SpeakerClassNames = {
  user: turnUser,
  userCompact: turnUserCompact,
  agentChat: turnAgentChat,
  agent: turnAgent,
};

const CARD_CLASSES: SpeakerClassNames = {
  user: cardUser,
  userCompact: cardUserCompact,
  agentChat: cardAgentChat,
  agent: cardAgent,
};

function speakerClassName(
  classNames: SpeakerClassNames,
  speaker: ConversationMessageSpeaker,
  isChat: boolean,
  density: 'default' | 'compact',
): string {
  if (speaker === 'user') {
    return density === 'compact' ? `${classNames.user} ${classNames.userCompact}` : classNames.user;
  }
  return isChat ? classNames.agentChat : classNames.agent;
}

export const ConversationMessageCard: FC<ConversationMessageCardProps> = ({
  speaker,
  variant = 'transcript',
  model,
  children,
  badges,
  headerExtra,
  footer,
  meta,
  metaTitle,
  className,
  hoverRevealCaption,
  density = 'default',
  redesign = false,
  time,
  copyText,
  onCopy,
  onShowDetails,
}) => {
  const roleId = useId();
  const tag = SPEAKER_TAG[speaker];
  const isChat = variant === 'chat';
  const plainAgent = redesign || isChat;
  const showTail = redesign && speaker === 'user';
  const turnClass = speakerClassName(TURN_CLASSES, speaker, isChat, density);
  const cardClass =
    speaker === 'user'
      ? [speakerClassName(CARD_CLASSES, speaker, isChat, density), showTail ? cardUserBubble : undefined]
          .filter(Boolean)
          .join(' ')
      : plainAgent
        ? cardAgentChat
        : cardAgent;
  const hasMeta = (meta != null && meta.length > 0) || Boolean(metaTitle);
  const showCopy = Boolean(copyText);
  const showFeedback = redesign && speaker === 'agent' && (showCopy || Boolean(onShowDetails));
  const showCaption = Boolean(footer) || hasMeta || Boolean(time) || showFeedback || (speaker === 'user' && showCopy);
  const showModel = speaker === 'agent' && Boolean(model);
  const showRole = !isChat && !redesign;
  const showHeader = showRole || showModel || Boolean(badges) || Boolean(headerExtra);
  const clock = time ? <span className={`${metaClass} ${metaItem}`}>{time}</span> : null;

  return (
    <div
      className={[
        turn,
        turnClass,
        redesign && speaker === 'agent' && !isChat ? cardTurnAgent : undefined,
        showCaption ? undefined : redesign ? turnCaptionless : undefined,
        redesign && hoverRevealCaption ? copyHoverGroup : undefined,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={[card, cardClass, showTail ? cardTail : undefined].filter(Boolean).join(' ')}>
        {showTail ? (
          <svg
            className={cardTailSvg}
            width={TAIL_SIZE}
            height={TAIL_SIZE}
            viewBox="0 0 13 13"
            aria-hidden
            focusable="false"
          >
            <path className={cardTailFill} d="M0.5 12.5 H12.5 L0.5 0.5 Z" />
            <path className={cardTailStroke} d="M0.5 12.5 H12.5 L0.5 0.5" />
          </svg>
        ) : null}
        {showHeader ? (
          <div className={cardHeader}>
            <div className={[cardHeaderMain, redesign ? cardHeaderMainWrap : undefined].filter(Boolean).join(' ')}>
              {showRole ? (
                <TagGroup aria-label={tag.label}>
                  <TagList>
                    <Tag size="small" id={roleId} textValue={tag.label}>
                      <Icon name={tag.icon} size="tiny" />
                      {tag.label}
                    </Tag>
                  </TagList>
                </TagGroup>
              ) : null}
              {showModel ? (
                <span className={modelChip} title={model}>
                  <Icon name="chip-ai" size="tiny" className={modelChipIcon} />
                  {model}
                </span>
              ) : null}
              {badges}
            </div>
            {headerExtra ? <div className={cardHeaderExtras}>{headerExtra}</div> : null}
          </div>
        ) : null}
        <div className={cardBody}>{children}</div>
      </div>
      {showCaption ? (
        <div
          className={[
            cardBelow,
            redesign ? cardBelowRedesign : undefined,
            speaker === 'user' ? cardBelowEnd : cardBelowStart,
            hoverRevealCaption ? (redesign ? copyAffordanceRedesign : copyAffordance) : undefined,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {speaker === 'agent' && clock}
          {showFeedback ? <MessageFeedback copyText={copyText} onCopy={onCopy} onShowDetails={onShowDetails} /> : null}
          {speaker === 'agent' ? footer : null}
          {hasMeta ? <MessageMeta items={meta ?? []} title={metaTitle} /> : null}
          {speaker === 'user' ? footer : null}
          {speaker === 'user' && showCopy && copyText ? (
            <span className={userCopy}>
              <CopyButton
                redesign
                text={copyText}
                label={msg('vega.conversation.copyUserMessageLabel')}
                successTitle={msg('vega.conversation.copyUserMessageSuccess')}
                onCopy={onCopy}
              />
            </span>
          ) : null}
          {speaker === 'user' && clock}
        </div>
      ) : null}
    </div>
  );
};
